import React, { useState, useEffect, useCallback } from 'react';
import { invRecipeService, invItemService, invUnitService } from '../../services/inventoryService';
import { restaurantService } from '../../services/dataService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtQty, fmtCost, friendlyErr } from './inventoryStyles';

const Recipes = ({ restaurant }) => {
  const [recipes, setRecipes]   = useState([]);
  const [menu, setMenu]         = useState([]);   // categories with menu_items
  const [invItems, setInvItems] = useState([]);
  const [units, setUnits]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [search, setSearch]     = useState('');
  const [form, setForm]         = useState({ menu_item_id: '', is_active: true, yield_qty: '1', notes: '' });
  const [ingredients, setIngredients] = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [rRes, mRes, iRes, uRes] = await Promise.all([
      invRecipeService.getAll(restaurantId),
      restaurantService.getMenu(restaurantId),
      invItemService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
    ]);
    setRecipes(rRes.data || []);
    setMenu(mRes.data || []);
    setInvItems((iRes.data || []).filter(i => i.is_active));
    setUnits(uRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const allMenuItems = menu.flatMap(c => (c.menu_items || []).map(mi => ({ ...mi, categoryName: c.name })));

  const openNew = () => {
    setEditing(null);
    setForm({ menu_item_id: '', is_active: true, yield_qty: '1', notes: '' });
    setIngredients([]);
    setError(''); setShowForm(true);
  };

  const openEdit = (recipe) => {
    setEditing(recipe);
    setForm({ menu_item_id: recipe.menu_item_id, is_active: recipe.is_active, yield_qty: String(recipe.yield_qty || 1), notes: recipe.notes || '' });
    setIngredients((recipe.items || []).map(ri => ({
      inventory_item_id: ri.inventory_item?.id || '',
      quantity: String(ri.quantity),
      unit_id: ri.unit?.id || '',
      wastage_percent: String(ri.wastage_percent || 0),
    })));
    setError(''); setShowForm(true);
  };

  const addIngredient = () => setIngredients(prev => [...prev, { inventory_item_id: '', quantity: '', unit_id: '', wastage_percent: '0' }]);
  const removeIngredient = (idx) => setIngredients(prev => prev.filter((_, i) => i !== idx));
  const updateIngredient = (idx, field, value) => setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing));

  const estimatedFoodCost = ingredients.reduce((sum, ing) => {
    const item = invItems.find(i => i.id === ing.inventory_item_id);
    const qty = parseFloat(ing.quantity) || 0;
    const wastage = 1 + (parseFloat(ing.wastage_percent) || 0) / 100;
    return sum + qty * wastage * (item?.avg_unit_cost || 0);
  }, 0);

  const linkedMenuItem = allMenuItems.find(m => m.id === form.menu_item_id);
  const foodCostPct = linkedMenuItem?.price && estimatedFoodCost > 0
    ? ((estimatedFoodCost / linkedMenuItem.price) * 100).toFixed(1)
    : null;

  const save = async () => {
    if (!form.menu_item_id) { setError('Select a menu item.'); return; }
    if (ingredients.some(i => !i.inventory_item_id || !i.quantity)) {
      setError('All ingredients need an item and quantity.'); return;
    }
    setSaving(true); setError('');
    const { error: e } = await invRecipeService.upsert(restaurantId, { ...form, id: editing?.id }, ingredients);
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false); load();
  };

  const toggleActive = async (recipe) => {
    await invRecipeService.toggleActive(recipe.id, !recipe.is_active);
    load();
  };

  const filtered = recipes.filter(r =>
    (r.menu_item?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Recipes</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>
              Map menu items to ingredient, stock auto deducts on billing
            </p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ New Recipe</button>
        </div>

        <div style={{ ...card, padding: '14px 20px', marginBottom: '20px' }}>
          <input placeholder="Search by menu item…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle(), width: '280px', padding: '8px 12px' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {loading ? (
            <div style={{ ...card, ...emptyState }}>Loading recipes…</div>
          ) : filtered.length === 0 ? (
            <div style={{ ...card, ...emptyState }}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No recipes yet</div>
              <div>Map menu items to ingredients to enable automatic stock deduction.</div>
            </div>
          ) : filtered.map(recipe => (
            <div key={recipe.id} style={{ ...card, padding: '20px', opacity: recipe.is_active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#111' }}>
                    {recipe.menu_item?.name || 'Unknown Item'}
                    {!recipe.is_active && <span style={{ marginLeft: '8px', fontSize: '0.75rem', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '10px' }}>Inactive</span>}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '2px' }}>
                    {recipe.items?.length || 0} ingredients · yield {recipe.yield_qty}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.82rem' }} onClick={() => openEdit(recipe)}>Edit</button>
                  <button style={{ ...btn(recipe.is_active ? 'danger' : 'green'), padding: '5px 12px', fontSize: '0.82rem' }} onClick={() => toggleActive(recipe)}>
                    {recipe.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(recipe.items || []).map((ri, i) => (
                  <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '0.82rem' }}>
                    <span style={{ fontWeight: '600', color: '#333' }}>{ri.inventory_item?.name}</span>
                    <span style={{ color: '#888', marginLeft: '6px' }}>{fmtQty(ri.quantity)} {ri.unit?.short_code || ''}</span>
                    {parseFloat(ri.wastage_percent) > 0 && <span style={{ color: '#c2410c', marginLeft: '4px' }}>(+{ri.wastage_percent}% waste)</span>}
                    {ri.inventory_item?.avg_unit_cost > 0 && (
                      <span style={{ color: '#16a34a', marginLeft: '6px' }}>{fmtCost(ri.quantity * ri.inventory_item.avg_unit_cost)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recipe Form Modal */}
      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '680px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>{editing ? 'Edit Recipe' : 'New Recipe'}</h3>
            {error && <div style={errorBox}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Menu Item *</label>
                <select style={selectStyle(!form.menu_item_id)} value={form.menu_item_id} onChange={e => setForm(f => ({ ...f, menu_item_id: e.target.value }))} disabled={!!editing}>
                  <option value="">Select menu item…</option>
                  {menu.map(cat => (
                    <optgroup key={cat.id} label={cat.name}>
                      {(cat.menu_items || []).map(mi => <option key={mi.id} value={mi.id}>{mi.name} — {fmtCost(mi.price)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Yield Qty</label>
                <input style={inputStyle()} type="number" min="0.001" step="0.001" value={form.yield_qty} onChange={e => setForm(f => ({ ...f, yield_qty: e.target.value }))} />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Notes</label>
                <input style={inputStyle()} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>

            {/* Ingredients */}
            <div style={{ marginTop: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ ...label, margin: 0 }}>Ingredients</label>
                <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.8rem' }} onClick={addIngredient}>+ Add Ingredient</button>
              </div>
              {ingredients.length === 0 && (
                <div style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '20px', border: '2px dashed #e5e7eb', borderRadius: '10px' }}>
                  No ingredients yet — add one above
                </div>
              )}
              {ingredients.map((ing, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '10px', alignItems: 'end' }}>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px' }}>Ingredient</label>}
                    <select style={selectStyle(!ing.inventory_item_id)} value={ing.inventory_item_id} onChange={e => updateIngredient(idx, 'inventory_item_id', e.target.value)}>
                      <option value="">Select item…</option>
                      {invItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px' }}>Qty</label>}
                    <input style={inputStyle(!ing.quantity)} type="number" min="0.001" step="0.001" value={ing.quantity} onChange={e => updateIngredient(idx, 'quantity', e.target.value)} placeholder="0.000" />
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px' }}>Unit</label>}
                    <select style={selectStyle()} value={ing.unit_id} onChange={e => updateIngredient(idx, 'unit_id', e.target.value)}>
                      <option value="">Base unit</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.short_code}</option>)}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px' }}>Waste %</label>}
                    <input style={inputStyle()} type="number" min="0" max="100" step="0.1" value={ing.wastage_percent} onChange={e => updateIngredient(idx, 'wastage_percent', e.target.value)} />
                  </div>
                  <button style={{ ...btn('danger'), padding: '8px 10px', fontSize: '0.82rem', alignSelf: 'flex-end' }} onClick={() => removeIngredient(idx)}>✕</button>
                </div>
              ))}
            </div>

            {/* Cost preview */}
            {estimatedFoodCost > 0 && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: '700', color: '#16a34a' }}>Estimated Food Cost: {fmtCost(estimatedFoodCost)}</span>
                {foodCostPct && <span style={{ color: '#666', marginLeft: '12px' }}>({foodCostPct}% of selling price)</span>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Recipe' : 'Create Recipe'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipes;
